/*
 * This kind of conversion could be done in the API. Putting it here for the time being.
 */
import { hgNumber, hgPrice, HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  Address,
  Attachment,
  AttachmentType,
  Buy,
  L10N,
  Listing,
  OfferType,
  Rent,
  Url,
  UrlType,
} from '@smg-real-estate/hg-rets-schema';
import { ListingItem } from '@smg-real-estate/hg-search-libs';
import { ComposerTranslation } from 'vue-i18n';

import {
  getCloudinaryUrlForTransformation,
  HgCloudinaryNamedTransformation,
} from '@/helpers/Cloudinary/CloudinaryHelper';
import { isListingEligibleForEnhancedSeoTitle } from '@/helpers/MetaInfo/MetaInfoHelper';
import { sanitizeUrl } from '@/helpers/Sanitize/Sanitize';
import { truncateText } from '@/helpers/TextFormatting/TextFormatting';
import {
  HgListingAttachedDocument,
  HgListingVideoMovie,
  HgListingVideoMovieTypes,
  HgListingVirtualTour,
  HgListingVirtualTourTypes,
} from '@/models';
import {
  getEmbeddedVideoUrl,
  getEmbeddedYouTubeUrl,
  isVideoMovieUrl,
  isYouTubeVideoUrl,
} from '@/models/HgListingVideoMovie';
import { getVirtualTourUrl, isVirtualTourUrl } from '@/models/HgListingVirtualTour';
import type { HgImage } from '@/store/types';

export interface HgListingImage extends HgImage {
  caption?: string;
  title?: string;
}

export function getListingDefaultLocale(listing: Listing | undefined): L10N | undefined {
  switch (listing?.localization?.primary) {
    case 'en':
      return listing.localization?.en;
    case 'it':
      return listing.localization?.it;
    case 'fr':
      return listing.localization?.fr;
    case 'de':
    default:
      return listing?.localization?.de;
  }
}

export const LISTING_LOCALIZATION_LANGUAGES = Object.values(
  HgSupportedLanguage,
) as HgSupportedLanguage[];

export function getListingLocaleForLanguage(
  listing: Listing | undefined,
  language: HgSupportedLanguage,
): L10N | undefined {
  if (!listing?.localization) {
    return undefined;
  }
  if (!LISTING_LOCALIZATION_LANGUAGES.includes(language)) {
    return undefined;
  }
  return listing.localization[language as 'de' | 'en' | 'fr' | 'it'];
}

/**
 * Resolves a listing title or description for the given UI locale.
 * Field-level fallback: each field is resolved independently — current locale
 * first, then the listing primary/default locale, then undefined.
 * Whitespace-only values are treated as empty.
 */
export function resolveListingLocalizedField(
  listing: Listing | undefined,
  language: HgSupportedLanguage,
  field: 'title' | 'description',
): string | undefined {
  const currentValue = getListingLocaleForLanguage(listing, language)?.text?.[field]?.trim();
  if (currentValue) {
    return currentValue;
  }

  const defaultValue = getListingDefaultLocale(listing)?.text?.[field]?.trim();
  return defaultValue || undefined;
}

export function getListingImages(listing: Listing): Attachment[] {
  return (
    getListingDefaultLocale(listing)?.attachments?.filter(
      (attachment) => attachment.type === AttachmentType.Image,
    ) || []
  );
}

export function getListingImage(listing: Listing, index: number): Attachment | undefined {
  return getListingImages(listing)[index];
}

export function getListingPriceData(listing: Listing): Buy | Rent | undefined {
  return listing.offerType === OfferType.Buy ? listing.prices?.buy : listing.prices?.rent;
}

export function getListingCurrency(listing: Listing): string | undefined {
  return listing.prices?.currency;
}

export function getListingPriceArea(listing: Listing): string | undefined {
  return getListingPriceData(listing)?.area;
}

export function getListingPriceInterval(listing: Listing): string | undefined {
  return listing.offerType === OfferType.Rent ? listing.prices?.rent?.interval : undefined;
}

export function getListingDisplayPrice(listing: Listing): number | undefined {
  const priceData = getListingPriceData(listing);

  if (!priceData) {
    return undefined;
  }

  return (priceData as Rent).gross ?? (priceData as Rent).net ?? (priceData as Buy).price;
}

export function getListingRooms(listing?: Listing): number | undefined {
  return listing?.characteristics?.numberOfRooms;
}

export function getListingLivingSpace(listing?: Listing): number | undefined {
  return listing?.characteristics?.livingSpace;
}

export function getListingLink(listingDefaultLocale: L10N): Url | null {
  return (
    listingDefaultLocale.urls?.find((url: Url) => {
      const isLink = url.type && url.type === UrlType.Link;
      // Ignore links with an unsafe scheme (e.g. javascript:) so they are never
      // surfaced as the listing link. Relative/fragment values are harmless and
      // preserved to keep existing links working.
      return (
        isLink &&
        url.value &&
        !isVirtualTourUrl(url.value) &&
        !!sanitizeUrl(url.value, { allowRelative: true })
      );
    }) ?? null
  );
}

const getListingUrls = (listingDefaultLocale: L10N | undefined): Url[] =>
  listingDefaultLocale?.urls ?? [];

const getListingAttachments = (listingDefaultLocale: L10N | undefined): Attachment[] =>
  listingDefaultLocale?.attachments ?? [];

export function getListingVirtualTour(
  listingDefaultLocale: L10N | undefined,
  locale = 'de',
): HgListingVirtualTour | undefined {
  const listingVirtualTours: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => url.type && url.type in HgListingVirtualTourTypes)
    .map((url) => ({
      // These URLs feed an iframe src directly, bypassing the provider allowlist,
      // so validate the scheme here to keep out javascript:/data: values.
      src:
        url.value && isYouTubeVideoUrl(url.value)
          ? getEmbeddedYouTubeUrl(url.value, locale)
          : sanitizeUrl(url.value, { allowRelative: true }),
    }))
    .filter((tour) => !!tour.src);

  if (listingVirtualTours.length > 0) {
    return listingVirtualTours[0];
  }

  // Additionally search in the urls for type LINK (legacy).
  const listingLinks: HgListingVirtualTour[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isLink = url.type && url.type === UrlType.Link;
      return isLink && url.value && isVirtualTourUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getVirtualTourUrl(url.value),
    }));

  return listingLinks.length > 0 ? listingLinks[0] : undefined;
}

export function getListingVideoMovie(
  listingDefaultLocale: L10N,
  locale = 'de',
): HgListingVideoMovie | undefined {
  const listingVideos: HgListingVideoMovie[] = getListingUrls(listingDefaultLocale)
    .filter((url) => {
      const isMovie = url.type && url.type in HgListingVideoMovieTypes;
      return isMovie && url.value && isVideoMovieUrl(url.value);
    })
    .map((url) => ({
      src: url.value && getEmbeddedVideoUrl(url.value, locale),
      title: url.title,
    }));

  if (listingVideos.length > 0) {
    return listingVideos[0];
  }

  // Additionally search in the attachment for videos that are a YouTube video.
  const listingMovies: HgListingVideoMovie[] = getListingAttachments(listingDefaultLocale)
    .filter((attachment) => {
      const isMovie = attachment.type && attachment.type === AttachmentType.Movie;
      return isMovie && attachment.url && isVideoMovieUrl(attachment.url);
    })
    .map((attachment) => ({
      src: attachment.url && getEmbeddedVideoUrl(attachment.url, locale),
      title: attachment.title,
    }));

  return listingMovies.length > 0 ? listingMovies[0] : undefined;
}

export function getListingAttachedDocument(
  listingDefaultLocale: L10N,
): HgListingAttachedDocument[] {
  return getListingAttachments(listingDefaultLocale)
    .filter((attachment: Attachment) => {
      return (
        attachment.type &&
        [AttachmentType.Document, AttachmentType.Plan, AttachmentType.SalesBrochure].includes(
          attachment.type,
        )
      );
    })
    .map((attachment: Attachment) => ({
      // Attachment URLs may be absolute or relative; drop any with an unsafe scheme.
      href: sanitizeUrl(attachment.url, { allowRelative: true }),
      label: attachment.title,
    }))
    .filter((document) => !!document.href);
}

export function getFirstListingAttachedDocument(
  listingDefaultLocale: L10N,
): HgListingAttachedDocument | undefined {
  const listingDocuments = getListingAttachedDocument(listingDefaultLocale);
  return listingDocuments.length > 0 ? listingDocuments[0] : undefined;
}

export const listingIdsFrom = (listings: ListingItem[]): string[] =>
  listings.map((listing) => listing.id || '');

export function getStreetWithAddition(address: Address): string | undefined {
  return address.streetAddition ? address.street + address.streetAddition : address.street;
}

export function getStreet(listing: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  return getStreetWithAddition(address);
}

export function getPostalCodeWithLocality(listing: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  return [address.postalCode, address.locality].filter((item) => !!item).join(', ');
}

export function getListingAddress(listing?: Listing): string | undefined {
  const address = listing?.address;

  if (!address) {
    return undefined;
  }

  const streetAndZip = [getStreetWithAddition(address), address.postalCode]
    .filter((item) => !!item)
    .join(', ');

  return [streetAndZip, address.locality].filter((item) => !!item).join(' ');
}

export function getListingFormattedPrice(listing: Listing, locale: string): string | null {
  const price = getListingDisplayPrice(listing);

  if (price === undefined || !listing.prices || price === 0) {
    return null;
  }
  return [listing.prices.currency, hgPrice(price, locale as HgSupportedLanguage)]
    .filter((item) => !!item)
    .join(' ');
}

export function getAvailabilityPrefix(listing: Listing, t: ComposerTranslation): string {
  if (isListingEligibleForEnhancedSeoTitle(listing)) {
    return `${t('availability.AVAILABILITY')} `;
  }
  return '';
}

export function getAvailability(listing: Listing, t: ComposerTranslation): string {
  if (!listing.availableFrom) {
    return t('availability.BY_AGREEMENT');
  }

  const availabilityDate = new Date(listing.availableFrom);
  if (availabilityDate.getTime() <= Date.now()) {
    return t('availability.IMMEDIATELY');
  }

  return availabilityDate.toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const wincasaListerIds = [
  'wic',
  'wicflf',
  '198822',
  'csbl',
  'isx1000225',
  'isx1089572',
  'isx1120201',
  'isx1158768',
  'isx192572',
  'isx312933',
  'isx46578',
  'jel',
  'wick',
  'wickar',
  'wicneulok',
  'wicNLgug',
  'wicp',
  'wict360',
  'wicvas',
  '198884',
  '198877',
  '198886',
  '198888',
  '198887',
  '198892',
  '198891',
  '198882',
  '198883',
  '198889',
  'jelge',
  '198885',
];

export const isWincasaListing = (listerId: string): boolean => {
  return wincasaListerIds.includes(listerId);
};

export function isNonSwissBorderRegion(postalCode: string | undefined): boolean {
  return postalCode === '8238';
}

export const getListingPrice = (listing: Listing, t: ComposerTranslation): string => {
  return listing
    ? `${getListingCurrency(listing)} ${getListingDisplayPrice(listing)}`
    : t('ON_REQUEST');
};

export const getListingFormattedRooms = (listing: Listing, t: ComposerTranslation): string => {
  const rooms = getListingRooms(listing);

  if (typeof rooms === 'undefined') {
    return '';
  }

  return `${rooms} ${t('ROOMS')}`;
};

export const getListingFormattedLivingSpace = (listing: Listing): string => {
  const livingSpace = getListingLivingSpace(listing);
  if (!livingSpace) {
    return '';
  }

  return hgNumber(livingSpace) + ' m²';
};

export const getListingTitle = (listing: Listing, t: ComposerTranslation): string => {
  const listingDefaultLocale = getListingDefaultLocale(listing);
  const listingTitle = listingDefaultLocale?.text?.title;
  const rooms = getListingFormattedRooms(listing, t);

  if (!listing?.address?.street || !getListingDisplayPrice(listing) || !rooms) {
    return listingTitle ?? '';
  }

  return t('metaInfo.pdp.titleTemplate', {
    listingTitle: listingTitle ? truncateText(listingTitle, 40) : '',
    location: (listing.address && getStreetWithAddition(listing.address)) || '',
    price: getListingPrice(listing, t),
    rooms: rooms,
  });
};

export const getExclusiveBlurryImageStartIndex = (listing?: Listing): number => {
  if (!listing) {
    return -1;
  }

  const listingImageAttachments = getListingImages(listing);

  if (listingImageAttachments.length === 0) {
    return -1;
  }

  if (
    listing.valueAddedServices?.numberOfImages &&
    listing.valueAddedServices.numberOfImages > listingImageAttachments.length
  ) {
    return listingImageAttachments.length;
  }

  return -1;
};

export const getExclusiveImageStartIndex = (listing?: Listing): number => {
  if (!listing) {
    return -1;
  }

  const listingImageAttachments = getListingImages(listing);

  if (listingImageAttachments.length === 0) {
    return -1;
  }

  if (
    listing.valueAddedServices?.numberOfImages &&
    listing.valueAddedServices.numberOfImages < listingImageAttachments.length
  ) {
    return listing.valueAddedServices.numberOfImages;
  }

  return -1;
};

const REPRESENTATIVE_BLURRED_EXCLUSIVE_IMAGE_URL =
  'https://media.homegate.ch/assets/representative_blurred_exclusive_image.png';

export const getListingImageAttachments = (listing?: Listing): HgListingImage[] | undefined => {
  if (!listing) {
    return undefined;
  }

  const listingImageAttachments = getListingImages(listing);
  if (!listingImageAttachments) {
    return undefined;
  }

  const listingTitle = getListingDefaultLocale(listing)?.text?.title ?? '';

  return listingImageAttachments
    .filter((attachment: Attachment) => !!attachment.url)
    .map((attachment: Attachment, index) => {
      const attachmentCaption =
        attachment.caption || attachment.title || `${listingTitle} (${index + 1})`;
      return {
        alt: attachment.alt || attachmentCaption,
        caption: attachmentCaption,
        src: attachment.url as string,
      };
    });
};

export const getDisplayingListingImageAttachments = (listing?: Listing): HgListingImage[] => {
  const hgImages = getListingImageAttachments(listing) ?? [];

  if (
    listing?.valueAddedServices?.numberOfImages &&
    listing.valueAddedServices.numberOfImages > hgImages.length
  ) {
    const originalNumberOfImages = hgImages.length;
    const numberOfImagesToAdd = listing.valueAddedServices.numberOfImages - hgImages.length;
    const listingTitle = getListingDefaultLocale(listing)?.text?.title ?? '';

    for (let i = 0; i < numberOfImagesToAdd; i++) {
      const imageAltTextAndCaption = `${listingTitle} (${originalNumberOfImages + i + 1})`;
      hgImages.push({
        alt: imageAltTextAndCaption,
        caption: imageAltTextAndCaption,
        src: REPRESENTATIVE_BLURRED_EXCLUSIVE_IMAGE_URL,
      });
    }
  }

  return hgImages;
};

export const getTransformedListingImageUrl = (listing: Listing): string | undefined => {
  const listingImages = getDisplayingListingImageAttachments(listing);
  if (!listingImages || listingImages.length === 0) {
    return undefined;
  }

  return getCloudinaryUrlForTransformation(
    listingImages[0].src,
    HgCloudinaryNamedTransformation.SMALL,
  );
};
